"use client";

import { usePathname } from "next/navigation";
import type { PropsWithChildren } from "react";
import { extractProjectSubdomain } from "@/lib/tenant";
import { getRootDomain, normalizeDomain } from "@/lib/urls";
import { ThemeProvider } from "./theme-provider";

const LIGHT_ONLY_PREFIXES = [
  "/signin",
  "/signup",
  "/contact",
  "/terms",
  "/privacy",
  "/linear",
];

function isLandingRoute(pathname: string): boolean {
  if (pathname === "/") return true;
  return LIGHT_ONLY_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

function cleanHostname(host: string): string {
  return host.split(",")[0]?.split(":")[0]?.trim().toLowerCase() ?? "";
}

function isPortalHost(host: string): boolean {
  const hostname = cleanHostname(host).replace(/^www\./, "");
  const rootDomain = normalizeDomain(getRootDomain()).replace(/^www\./, "");

  if (
    !hostname ||
    hostname === "localhost" ||
    /^127\./.test(hostname) ||
    hostname === "0.0.0.0" ||
    hostname === "[::1]" ||
    hostname.endsWith(".vercel.app")
  ) {
    return false;
  }

  if (extractProjectSubdomain(hostname, rootDomain)) {
    return true;
  }

  if (hostname === rootDomain || hostname.endsWith(`.${rootDomain}`)) {
    return false;
  }

  return true;
}

function shouldForceLightTheme(pathname: string): boolean {
  if (typeof window !== "undefined" && isPortalHost(window.location.host)) {
    return false;
  }

  return isLandingRoute(pathname);
}

export function AppThemeProvider({ children }: PropsWithChildren) {
  const pathname = usePathname();
  const forcedTheme = shouldForceLightTheme(pathname) ? "light" : undefined;

  return <ThemeProvider forcedTheme={forcedTheme}>{children}</ThemeProvider>;
}
